/**
 * Azure AD B2C wiring for the RCP portal.
 *
 * Identity provider: Email OTP custom policy (no password). Policy name defaults
 * to `B2C_1A_PCB_EmailOTP_SignUpSignIn`. The UI never asks for an OTP directly —
 * MSAL redirects to the B2C hosted page, the user enters the OTP there, and we
 * catch the token in the redirect callback.
 *
 * See docs/gap-search/rcp-security-and-accessibility.md §A.4 #1.
 */

import {
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
  type AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { ENV } from '../constants/env';

function buildB2cConfig(): Configuration {
  const authorityHost = `https://${ENV.b2cTenant}.b2clogin.com`;
  const authority = `${authorityHost}/${ENV.b2cTenant}.onmicrosoft.com/${ENV.b2cPolicy}`;
  return {
    auth: {
      clientId: ENV.b2cClientId,
      authority,
      knownAuthorities: [`${ENV.b2cTenant}.b2clogin.com`],
      redirectUri: ENV.b2cRedirectUri,
      postLogoutRedirectUri: ENV.b2cPostLogoutRedirectUri,
    },
    cache: {
      // sessionStorage: tokens cleared when the tab closes.
      // NEVER localStorage — per rcp-security §A.6 "PII in browser storage".
      cacheLocation: 'sessionStorage',
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        piiLoggingEnabled: false,
      },
    },
  };
}

/**
 * Scope we request. For B2C, the scope is the registered app's client-id scope;
 * the custom-policy relying party returns an id_token we use as our bearer.
 * In v3.1 (Banno SSO) this becomes a second MSAL instance.
 */
export const B2C_SCOPES: ReadonlyArray<string> = ['openid', 'profile', 'offline_access'];

let _msal: PublicClientApplication | null = null;
let _initPromise: Promise<void> | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!_msal) _msal = new PublicClientApplication(buildB2cConfig());
  return _msal;
}

export async function initializeMsal(): Promise<void> {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const msal = getMsalInstance();
    await msal.initialize();
    // Handle the redirect if we just came back from B2C.
    try {
      await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[RCP] B2C redirect parse failed', e);
    }
  })();
  return _initPromise;
}

export async function login(returnTo?: string): Promise<void> {
  await initializeMsal();
  const req: RedirectRequest = {
    scopes: [...B2C_SCOPES],
    ...(returnTo ? { state: returnTo } : {}),
  };
  await getMsalInstance().loginRedirect(req);
}

export async function logout(): Promise<void> {
  await initializeMsal();
  const msal = getMsalInstance();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  await msal.logoutRedirect({
    ...(account ? { account } : {}),
    postLogoutRedirectUri: ENV.b2cPostLogoutRedirectUri,
  });
}

/**
 * Get a bearer token (id_token) silently, falling back to interactive redirect
 * if the cache is cold. Returns `undefined` when the caller is unauthenticated
 * and the app chose NOT to force login (e.g., landing page).
 */
export async function getAccessToken(): Promise<string | undefined> {
  await initializeMsal();
  const msal = getMsalInstance();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return undefined;
  try {
    const result: AuthenticationResult = await msal.acquireTokenSilent({
      scopes: [...B2C_SCOPES],
      account,
    });
    // B2C custom-policy apps typically use the id_token as the bearer to the
    // Function. If access_token is present and the Function is configured for it,
    // prefer access_token; otherwise fall back to id_token.
    return result.accessToken && result.accessToken.length > 10
      ? result.accessToken
      : result.idToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [...B2C_SCOPES], account });
    }
    return undefined;
  }
}

export function getActiveAccountEmail(): string | undefined {
  const msal = _msal;
  if (!msal) return undefined;
  const acct = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!acct) return undefined;
  // B2C email is typically in idTokenClaims.emails[0] or acct.username.
  const claims = (acct.idTokenClaims ?? {}) as { emails?: string[]; email?: string };
  return claims.emails?.[0] ?? claims.email ?? acct.username;
}

export function getActiveAccountSub(): string | undefined {
  const msal = _msal;
  if (!msal) return undefined;
  const acct = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  const claims = (acct?.idTokenClaims ?? {}) as { sub?: string; oid?: string };
  return claims.sub ?? claims.oid ?? acct?.localAccountId;
}
